// timezone — resolving the browser's IANA zone to an entry the report filters
// can actually use.
//
// History: the report filters seeded their timezone by looking up
// `moment.tz.guess()` in the `timezones-list` package and, on a miss, fell back
// to `{ timezoneName: "", timezoneLabel: "", timezoneCode: "", timezoneUTC: "" }`.
// An empty `timezoneCode` is not a cosmetic problem: every report chart and the
// report table gate their fetch on `filters.timezoneCode !== ""`, so a miss
// leaves the whole reports page permanently blank with no error shown.
//
// A miss is not exotic. `timezones-list` is a 424-entry snapshot of the IANA
// database, and `moment.tz.guess()` returns whatever the browser's
// `Intl.DateTimeFormat().resolvedOptions().timeZone` reports whenever moment has
// data for it. Verified misses include:
//   - `Etc/UTC`, `Etc/GMT` — containers and Linux desktops set to UTC (the
//     package has no `UTC` entry and no `Etc/*` entries at all)
//   - `Asia/Calcutta` and other IANA link names the snapshot did not record
//   - `Pacific/Kanton` and any other zone added to IANA after the snapshot
//
// (A browser reporting the bare string `UTC` does *not* miss: moment's guess
// ignores Intl names of 3 characters or fewer and falls through to its
// offset-scoring heuristic, which lands on a listed zone such as
// `Africa/Abidjan`. `Etc/UTC` is longer, so it passes that guard and misses.)
//
// So the fallback has to answer for "the guessed zone is not in the list",
// not just for UTC.

// TIMEZONE
import timezones from "timezones-list";
import moment from "moment-timezone";
// INTERFACES
import { TimeZoneInterface } from "../interfaces/Reports.interface";

/**
 * `timezones-list` has no UTC entry, so we add one. It gives UTC an honest
 * identity in the timezone picker instead of presenting a UTC user with an
 * arbitrary same-offset city, and it is the last-resort answer below.
 */
export const UTC_TIMEZONE: TimeZoneInterface = {
  label: "UTC (GMT+00:00)",
  name: "(GMT+00:00) UTC",
  tzCode: "UTC",
  utc: "+00:00",
};

// `timezones-list` is ordered by UTC offset. Slot UTC in at the head of the
// +00:00 block so the picker stays sorted and so UTC — not Africa/Abidjan —
// wins the offset lookup below for zones like Etc/UTC.
const utcBlockStart = timezones.findIndex((zone) => zone.utc === UTC_TIMEZONE.utc);
const utcInsertAt = utcBlockStart >= 0 ? utcBlockStart : timezones.length;

/** The timezone list the app offers and resolves against: `timezones-list` plus UTC. */
export const appTimezones: TimeZoneInterface[] = [
  ...timezones.slice(0, utcInsertAt),
  UTC_TIMEZONE,
  ...timezones.slice(utcInsertAt),
];

/** This zone right now, or null when moment has no data for the name. */
function zoneNow(tzCode: string): moment.Moment | null {
  return moment.tz.zone(tzCode) ? moment.tz(tzCode) : null;
}

/**
 * Resolve an IANA zone name to a listed timezone entry. Never returns empty
 * fields — every branch yields a usable `tzCode`.
 *
 * Resolution order:
 *   1. exact `tzCode` match (the common case, and the only branch most users hit)
 *   2. a listed zone at the same offset *at this instant* — measured live on both
 *      sides rather than read off the entry's `utc` string, which records standard
 *      time only and would mismatch every DST-observing zone for half the year.
 *      Zones currently on DST are considered only if nothing else fits: they hold
 *      that offset for a season, so matching one would silently shift the user's
 *      reports at the next changeover. Without this, an `Etc/UTC` browser resolves
 *      to Atlantic/Azores every summer, since Azores keeps +00:00 until October.
 *   3. UTC, for a name moment itself does not recognise
 */
export function resolveTimezone(tzCode: string): TimeZoneInterface {
  const exact = appTimezones.find((zone) => zone.tzCode === tzCode);
  if (exact) return exact;

  const target = zoneNow(tzCode);
  if (target) {
    const offset = target.utcOffset();
    const candidates = appTimezones
      .map((zone) => ({ zone, now: zoneNow(zone.tzCode) }))
      .filter((entry) => entry.now?.utcOffset() === offset);

    const yearRound = candidates.find((entry) => !entry.now?.isDST());
    if (yearRound) return yearRound.zone;
    if (candidates.length > 0) return candidates[0].zone;
  }

  return UTC_TIMEZONE;
}

/** The browser's own timezone, resolved to a listed entry. */
export function getLocalTimezone(): TimeZoneInterface {
  return resolveTimezone(moment.tz.guess());
}

export default resolveTimezone;
